import { CdkDragDrop, transferArrayItem } from '@angular/cdk/drag-drop';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { OrderByPipe } from 'ngx-pipes';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthService, SettingsService } from '@app/core/auth';
import { ClientService } from '@app/core/client/services/client.service';
import { PipelineService } from '@app/core/pipelines/services/pipeline.service';
import { ProductTagService } from '@app/core/product-tags/services/product-tag.service';
import { CategoryService } from '@app/core/products/services/category.service';
import { ProductDesignService } from '@app/core/quotes/services/product-design.service';
import { StaffService } from '@app/core/staff';

import { NdPopupFormComponent } from '../nd-popup-form/nd-popup-form.component';
import { NdViewCardPopupComponent } from '../nd-view-card-popup/nd-view-card-popup.component';

@Component({
  selector: 'cnc-nd-master-pipeline-view',
  templateUrl: './nd-master-pipeline-view.component.html',
  styleUrls: ['./nd-master-pipeline-view.component.scss'],
})
export class NdMasterPipelineViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() activePipeline :any;
  @Input() filter :any;
  @Input() pipelines :any;

  @Output() setMasterPipelineCompletion = new EventEmitter<any[]>();
  @Output() setActivePipeline = new EventEmitter<any[]>();

  subscriptions :Subscription[] = [];

  constructor(
    public authService :AuthService,
    public settingsSvc :SettingsService,
    private ref :ChangeDetectorRef,
    private modal :NgbModal,
    private translate :TranslateService,
    public pcatService :CategoryService,
    public staffService :StaffService,
    public clientSvc :ClientService,
    private toastr :ToastrService,
    public pipelineSvc :PipelineService,
    public productDesignSvc :ProductDesignService,
    private orderBy :OrderByPipe,
    public tagSvc :ProductTagService,
  ) {}

  ngOnChanges(changes :SimpleChanges) {
    if (changes.filter && !changes.firstChange) {
      this.activePipeline.child_pipelines.forEach((pipeline) => {
        pipeline.records = [];
        this.getPipelineTasks(pipeline);
      });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub :Subscription) => {
      sub.unsubscribe();
    });
  }

  ngOnInit() {}

  getPipelineTasks(pipeline :any) {
    pipeline.loading = true;
    this.filter = Object.entries(this.filter).reduce(
      (a, [k, v]) => (v ? ((a[k] = v), a) : a),
      {},
    );

    const tags = [];

    Object.keys(this.filter).forEach((e) => {
      if (e == 'assigned_tags') {
        tags.push(this.filter[e]);
      }
    });

    const params = {
      all: 1,
      pipeline: pipeline.pipeline_id,
      ...this.filter,
    };
    delete params.pipelineId;

    if (tags && tags.length > 0) {
      params.assigned_tags = tags.join(',');
    } else {
      delete params.assigned_tags;
    }

    this.subscriptions.push(
      this.productDesignSvc.getAsync(params).subscribe((designs) => {
        pipeline.records = designs.map((row) => {
          return {
            ...row,
            attachments_count: row.attachments.length,
            priority: row.pipelines.find(
              (p) => p.pipeline_id == pipeline.pipeline_id,
            ).priority,
          };
        });
        pipeline.recordsRaw = pipeline.records;

        pipeline.count = pipeline.records.length;
        // this.filterpipeline(pipeline);
        pipeline.tags = [];
        if (pipeline.tag_ids) {
          pipeline.tags = this.activePipeline.tags.filter(
            (row) => pipeline.tag_ids.indexOf(row.id) >= 0,
          );
        }

        try {
          pipeline.owners = JSON.parse(pipeline.owners);
        } catch (e) {}

        pipeline.assignees = [];
        this.computePipelineCompletion(pipeline);
        pipeline.loading = false;
        this.ref.detectChanges();
      }),
    );
  }

  markAsCompleted(design :any, i :number) {
    const data = {
      mark_as_completed: design.mark_as_completed,
      design_name: design.design_name,
    };
    this.productDesignSvc.markAsCompleted(design.id, data).subscribe(
      (resp) => {},
      (err) => {},
    );
    const pipeline = this.activePipeline.child_pipelines[i];
    this.computePipelineCompletion(pipeline);
  }

  isDesignCompleted(markAsCompleted) {
    return markAsCompleted;
  }

  isDesignDue(dueDate :Date, markAsCompleted) {
    if (markAsCompleted) {
      return false;
    }
    const today :Date = new Date();
    const dateDue :Date = new Date(dueDate);

    return today >= dateDue;
  }

  isDesignSubtaskCompleted(subtasks) {
    let completed = true;

    subtasks.map((subtask, i) => {
      if (subtask.done) {
        completed = true;
      } else {
        completed = false;
      }
    });

    return completed;
  }

  checkCompleteSubtask(subtasks) {
    let count = 0;

    subtasks.map((subtask, i) => {
      if (subtask.done) {
        count++;
      }
    });

    return count;
  }

  toggleTag(tagId, activePipeline) {
    if (!activePipeline.tag_ids) {
      activePipeline.tag_ids = [tagId];
    } else {
      const idx = activePipeline.tag_ids.indexOf(tagId);
      if (idx >= 0) {
        activePipeline.tag_ids.splice(idx, 1);
      } else {
        activePipeline.tag_ids.push(tagId);
      }
    }
    this.productDesignSvc
      .updateDesignPipelineStage(
        activePipeline.product_design_id,
        this.activePipeline.id,
        { tag_ids: activePipeline.tag_ids },
      )
      .subscribe();
  }

  // compute for pipeline completion status
  computeMasterPipelineCompletion() {
    const status :any = {
      total: 0,
      completed: 0,
    };
    this.activePipeline.child_pipelines.forEach((pipeline :any) => {
      status.total += pipeline.completionStatus
        ? pipeline.completionStatus.total
        : 0;
      status.completed += pipeline.completionStatus
        ? pipeline.completionStatus.completed
        : 0;
    });
    this.activePipeline.completionStatus = status;
    this.setMasterPipelineCompletion.emit(status);
  }

  drop(event :CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
    } else {
      const sourceChildPipeline :any =
        this.activePipeline.child_pipelines[event.previousContainer.id];
      const destChildPipeline :any =
        this.activePipeline.child_pipelines[event.container.id];
      const design :any = event.item.data;

      const destPipeline = this.pipelines.find(
        (row) => row.id == destChildPipeline.pipeline_id,
      );
      const sourcePipeline = design.pipelines.filter(
        (row) => row.pipeline_id == sourceChildPipeline.pipeline_id,
      );
      const sourcePipelines = design.pipelines.filter(
        (row) => row.pipeline_id != sourceChildPipeline.pipeline_id,
      );

      const pipelines :any[] = [];
      sourcePipelines.forEach((p) => {
        pipelines.push({
          pipeline_id: p.pipeline_id,
          pipeline_stage_id: p.pipeline_stage_id,
          priority: p.priority,
        });
      });

      pipelines.push({
        pipeline_id: destPipeline.id,
        pipeline_stage_id: destPipeline.stages[0].id,
        priority: sourcePipeline.priority,
      });

      this.productDesignSvc
        .setDesignPipelines(design.id, { pipelines: pipelines })
        .subscribe();
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      this.computePipelineCompletion(sourceChildPipeline);
      this.computePipelineCompletion(destChildPipeline);
    }
  }

  // compute for pipeline stage completion status
  computePipelineCompletion(pipeline :any) {
    // set to count for complete status of this stage
    pipeline.completionStatus = {
      total: 0,
      completed: 0,
    };
    pipeline.records.forEach((record :any) => {
      if (!record.assigned_subtasks || record.assigned_subtasks.length == 0) {
        pipeline.completionStatus.total += 1;
        pipeline.completionStatus.completed += record.mark_as_completed ? 1 : 0;
      } else {
        pipeline.completionStatus.total += record.assigned_subtasks.length;
        pipeline.completionStatus.completed += record.mark_as_completed
          ? record.assigned_subtasks.length
          : record.assigned_subtasks.filter((row) => row.done).length;
      }
    });
    this.computeMasterPipelineCompletion();
  }

  editdesign(design :any, i :number) {
    const modalRef = this.modal.open(NdPopupFormComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.design = design;
    modalRef.result.then(
      (newDesign :any) => {
        if (newDesign && newDesign.id) {
          const pipeline = this.activePipeline.child_pipelines[i];
          const idx = pipeline.records.findIndex((row) => row.id == newDesign.id);
          pipeline.records.splice(
            idx,
            1,
            Object.assign(pipeline.records[idx], newDesign),
          );
          this.computePipelineCompletion(pipeline);
          this.ref.detectChanges();
        }
      },
      (_) => {},
    );
  }

  deletedesign(design :any, i :number) {
    Swal.fire({
      title: 'Confirm delete?',
      text: "Delete design '" + design.design_name + "'?",
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.translate.instant('BUTTONS.CONFIRM_DELETE_TEXT'),
    }).then((result) => {
      if (result.value) {
        this.productDesignSvc
          .deleteproductDesignPipeline(design.id)
          .subscribe((resp :any) => {
            const pipeline = this.activePipeline.child_pipelines[i];
            const idx = pipeline.records.findIndex((row) => row.id == design.id);
            pipeline.records.splice(idx, 1);
            this.ref.detectChanges();
            this.toastr.success('Successfully deleted design.', 'Success');
          });
      }
    });
  }

  showDetails(design :any, i :number, tabIndex :number = 0) {
    // this.router.navigate(["/quotes", "new-designs", design.id, "view"]);
    const modalRef = this.modal.open(NdViewCardPopupComponent, { size: 'lg' });
    modalRef.componentInstance.designCard = design;
    modalRef.componentInstance.tabIndex = tabIndex;

    modalRef.result.then(
      (result :any) => {
        if (result.method && result.method == 'delete') {
          const pipeline = this.activePipeline.child_pipelines[i];
          const idx = pipeline.records.findIndex((row) => row.id == design.id);
          pipeline.records.splice(idx, 1);
          this.ref.detectChanges();

          return;
        }
        if (result && result.id) {
          const pipeline = this.activePipeline.child_pipelines[i];
          const idx = pipeline.records.findIndex((row) => row.id == result.id);
          pipeline.records.splice(
            idx,
            1,
            Object.assign(pipeline.records[idx], result),
          );
          this.ref.detectChanges();
        }
      },
      (_) => {},
    );

    // window.open('/quotes/new-designs/' + design.id + '/view');
  }

  sortPipeline(pipeline) {
    pipeline.records = this.orderBy.transform(pipeline.records, ['-priority']);
  }

  getPriorityClass(design, pipelineId) {
    const priority = design.priority
      ? design.priority
      : design.pipelines.filter(
          (pipeline) => pipeline.pipeline_id === pipelineId,
        )[0].priority;

    return priority == 30 ? 'high' : priority == 20 ? 'medium' : 'low';
  }

  abilityToDelete(design) {
    if (this.authService.loginUser.role == 99) {
      return true;
    }
    if (design.created_by == null) {
      return false;
    }
    if (design.created_by.id == this.authService.loginUser.id) {
      return true;
    } else {
      return false;
    }
  }

  calculateDateDiff(dateCreated) {
    const currentDate = new Date();
    const dateSent = new Date(dateCreated);

    const days = Math.floor(
      (Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
      ) -
        Date.UTC(dateSent.getFullYear(), dateSent.getMonth(), dateSent.getDate())) /
        (1000 * 60 * 60 * 24),
    );

    return days;
  }
}
